import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { baseGrid } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import { label, paragraphLarge } from '@helpers/typography';
import styled from 'styled-components';
const Container = styled.section `
  --fgColour: ${brand.primary.purple};

  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: ${Layers.Header + 1};

  /* Ensure space doesn't interfere with content beneath */
  pointer-events: none;

  &[data-type='alert'] {
    --bgColour: ${brand.secondary.red};
    --buttonColour: ${brand.primary.purple};
  }

  &[data-type='notification'] {
    --bgColour: ${brand.primary.offWhite};
    --buttonColour: ${brand.tertiary.darkBlue};
  }

  @media ${from(Device.TabletLarge)} {
    ${baseGrid};
  }

  .equally-ai___high-saturation & {
    filter: saturate(200%);
  }

  .equally-ai___reverse-contrast & {
    filter: invert(100%);
  }

  .equally-ai___monochrome-contrast & {
    filter: grayscale(100%);
  }

  .equally-ai___reverse-contrast.equally-ai___monochrome-contrast & {
    filter: grayscale(100%) invert(100%);
  }
`;
const Banner = styled.div `
  ${transition('opacity, transform', 1.25)};

  position: relative;
  pointer-events: all;
  background-color: var(--bgColour);
  color: var(--fgColour);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${fluid(8, 60)};
  padding: ${fluid(16, 26)} 38px;
  text-align: center;

  @media ${from(Device.TabletLarge)} {
    grid-column: 2 / span 10;
    flex-direction: row;
    justify-content: center;
    text-align: left;
    padding-left: ${fluid(62, 80)};
    padding-right: ${fluid(62, 80)};
  }

  ${Container}[data-visible='true'] & {
    transform: translateY(0);
    opacity: 1;
  }

  ${Container}[data-visible='false'] & {
    transform: translateY(100%);
    opacity: 0;
  }
`;
const Label = styled.p `
  ${label};

  margin: 0 0 8px;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 6px;
  }
`;
const BannerText = styled.p `
  ${paragraphLarge};

  margin: 0;

  > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: ${fluid(34, 50)};
    width: ${fluid(28, 42)};
    font-size: ${fluid(22, 24)};
    background-color: ${brand.primary.offWhite};
    box-shadow: inset 0px 0px 6px 3px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    margin: 0 ${fluid(2, 3)};
  }
`;
const Divider = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    display: block;
    width: 2px;
    height: ${fluid(42, 50)};
    background-color: currentColor;
  }
`;
const Close = styled.button `
  ${ButtonReset};

  position: absolute;
  display: grid;
  place-content: center;
  width: 48px;
  height: 48px;
  top: 2px;
  right: 2px;
  color: var(--buttonColour);

  > svg {
    ${transition('transform')};

    display: block;
    height: 16px;
    width: 16px;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &:hover {
    > svg {
      transform: scale(1.15);
    }
  }

  @media ${from(Device.TabletLarge)} {
    top: 50%;
    right: ${fluid(8, 24)};
    transform: translateY(-50%);
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }
`;
const AlertsBannerStyles = {
    Container,
    Banner,
    Close,
    BannerText,
    Label,
    Divider,
};
export default AlertsBannerStyles;
